<template>
    <div class="step-intent-mapping">
        <h4 class="step-title">{{ mappedIntents.length > 0 ? 'IVR to AI Mappings' : 'Map IVR Options to AI Intents' }}</h4>
        <p class="step-description">
            {{ mappedIntents.length > 0 
                ? 'We\'ve analyzed your IVR menu and mapped each option to AI agent actions.' 
                : 'We\'re mapping your IVR options to intelligent AI actions. This will help your AI agent understand and handle different menu choices.'
            }}
        </p>

        <div class="intents-table">
            <el-table
                :data="mappedIntents"
                border
                style="width: 100%">
                <el-table-column
                    label="IVR Option"
                    width="120">
                    <template slot-scope="scope">
                        <el-tag v-if="scope.row.original_key" size="small">
                            Option {{ scope.row.original_key }}
                        </el-tag>
                        <span v-else class="text-muted">—</span>
                    </template>
                </el-table-column>

                <el-table-column
                    label="Intent"
                    prop="intent"
                    width="180">
                </el-table-column>

                <el-table-column
                    label="Action / Tool"
                    min-width="200">
                    <template slot-scope="scope">
                        <div>
                            <div>
                                <strong>{{ formatAction(scope.row.action) }}</strong>
                            </div>
                            <div v-if="scope.row.details" class="text-muted small">
                                {{ scope.row.details }}
                            </div>
                            <div v-if="scope.row.tool_pattern" class="tool-pattern">
                                <code>{{ scope.row.tool_pattern }}</code>
                            </div>
                            <div v-if="scope.row.recommendation" class="recommendation-text">
                                <i class="el-icon-warning"></i>
                                {{ scope.row.recommendation }}
                            </div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column
                    label="Status"
                    width="120"
                    align="center">
                    <template slot-scope="scope">
                        <el-tag
                            :type="getStatusType(scope.row.status)"
                            size="small">
                            {{ scope.row.status === 'supported' ? '✓ Supported' : '⚠ Unsupported' }}
                        </el-tag>
                    </template>
                </el-table-column>
            </el-table>

            <div v-if="supportedCount === 0" class="mt-3">
                <el-alert
                    type="error"
                    :closable="false"
                    show-icon>
                    <template slot="title">No Supported Actions</template>
                    At least one supported action is required to proceed. Please ensure your IVR has compatible routing options.
                </el-alert>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Step2IntentMapping',

    props: {
        ivrId: {
            type: Number,
            required: true
        },
        analysisData: {
            type: Object,
            default: null
        },
        wizardData: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            conversionResult: null,
            mappedIntents: []
        }
    },

    computed: {
        supportedCount() {
            return this.mappedIntents.filter(i => i.status === 'supported').length
        },

        unsupportedCount() {
            return this.mappedIntents.filter(i => i.status === 'unsupported').length
        }
    },

    mounted() {
        if (this.wizardData.mapped_intents && this.wizardData.mapped_intents.length > 0) {
            this.mappedIntents = [...this.wizardData.mapped_intents]
            this.emitUpdate()
        }
    },

    methods: {
        formatAction(action) {
            return action.split('_').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')
        },

        getStatusType(status) {
            return status === 'unsupported' ? 'danger' : 'success'
        },

        emitUpdate() {
            this.$emit('update', {
                conversionResult: this.conversionResult,
                mapped_intents: this.mappedIntents
            })
        },

        validate() {
            if (this.supportedCount === 0) {
                this.$message.warning('At least one supported action is required')
                return false
            }
            return true
        }
    },

    watch: {
        'wizardData.mapped_intents': {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    this.mappedIntents = [...newVal]
                }
            },
            immediate: true,
            deep: true
        }
    }
}
</script>

<style scoped>
.step-intent-mapping {
    padding: 10px 0 0 0;
    padding-bottom: 0px;
}

.step-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
}

.step-description {
    color: #666;
    margin-bottom: 15px;
    word-break: keep-all;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.tool-pattern {
    margin-top: 5px;
    padding: 4px 8px;
    background: #f5f7fa;
    border-radius: 3px;
    font-size: 12px;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.tool-pattern code {
    color: #409eff;
    font-family: 'Monaco', 'Consolas', monospace;
    word-break: break-all;
}

.recommendation-text {
    margin-top: 5px;
    padding: 4px 8px;
    background: #fff3cd;
    border-left: 3px solid #ffc107;
    font-size: 12px;
    color: #856404;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
    line-height: 1.5;
}

.recommendation-text i {
    margin-right: 5px;
}

.text-muted {
    color: #909399;
}

.small {
    font-size: 12px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
    line-height: 1.5;
}

.intents-table >>> .el-table__body-wrapper .el-table__cell {
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
}

.intents-table >>> .el-table__body-wrapper td {
    white-space: normal !important;
}
</style>
